import React, { useState, useEffect } from "react";
import { Card, ListGroup, Dropdown, Spinner } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import { useStore } from "react-redux";
import GoalIcon from "../assets/goal-1.png"; // replace with your goal icon
import { resolveName } from "../utils/messageUtils";

function GoalCard({
    title,
    inContext,
    goalData,
    fhirClient,
    patientId,
    enableSearch = false,
    onGoalChange,
    isReply,
    depth = 6,
}) {
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalGoalDetails, setModalGoalDetails] = useState(null);
    const [goalHistory, setGoalHistory] = useState([]);
    const [selectedGoal, setSelectedGoal] = useState(goalData || null);
    const [historyLoading, setHistoryLoading] = useState(false);

    const handleModalClose = () => setShowModal(false);
    const store = useStore();

    if (!title) {
        title = resolveName(goalData, fhirClient, store);
    }

    /** Sync incoming goal */
    useEffect(() => {
        if (goalData?.error) {
            setError(`Error loading goal (code: ${goalData.error}).`);
            setSelectedGoal(null);
        } else {
            setError("");
            setSelectedGoal(goalData || null);
        }
    }, [goalData]);

    /** Fetch patient goal history */
    useEffect(() => {
        if (!enableSearch || !patientId || !fhirClient) return;

        const fetchGoals = async () => {
            setHistoryLoading(true);
            try {
                const response = await fhirClient.request(
                    `Goal?patient=Patient/${patientId}&_sort=-start-date&_count=10`
                );
                const found = response.entry?.map((e) => e.resource) || [];
                setGoalHistory(found);
            } catch (e) {
                console.warn("Goal history fetch error:", e);
                setGoalHistory([]);
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchGoals();
    }, [enableSearch, patientId, fhirClient]);

    /** Auto-select newest goal */
    useEffect(() => {
        if (!isReply && enableSearch) {
            if (goalHistory.length > 0) {
                const first = goalHistory[0];
                setSelectedGoal(first);
                if (onGoalChange) onGoalChange(first);
            } else {
                setSelectedGoal(null);
                if (onGoalChange) onGoalChange(null);
            }
        }
    }, [goalHistory]);

    /** Open modal with fully loaded Goal */
    const handleModalOpen = async () => {
        if (!selectedGoal?.id) return;

        try {
            const details = await fhirClient.request(`Goal/${selectedGoal.id}`);
            setModalGoalDetails(details);
        } catch {
            setModalGoalDetails(null);
        }
        setShowModal(true);
    };

    const handleSelectGoal = (g) => {
        setSelectedGoal(g);
        if (onGoalChange) onGoalChange(g);
    };

    /** Field rendering */
    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="description">
            <strong>Description:</strong>{" "}
            {selectedGoal?.description?.text || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="lifecycleStatus">
            <strong>Status:</strong>{" "}
            {selectedGoal?.lifecycleStatus || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="achievementStatus">
            <strong>Achievement:</strong>{" "}
            {selectedGoal?.achievementStatus?.text || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="start">
            <strong>Start:</strong>{" "}
            {formatLocalDate(selectedGoal?.startDate)}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="target">
            <strong>Target:</strong>{" "}
            {selectedGoal?.target?.[0]?.dueDate
                ? formatLocalDate(selectedGoal.target[0].dueDate)
                : "N/A"}
        </ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                {/* HEADER */}
                <Card.Header
                    className={`d-flex justify-content-between align-items-center ${
                        inContext ? styles.inContextCardHeader : styles.cardHeader
                    }`}
                    onClick={handleModalOpen}
                    style={{ cursor: "pointer" }}
                >
                    {/* LEFT SIDE */}
                    <div className="d-flex align-items-center" style={{ pointerEvents: "none" }}>
                        <img src={GoalIcon} alt="Goal Icon" className={styles.fhirIcon} />

                        <h6 className="mb-0" style={{ marginLeft: "0.5rem" }}>
                            {title?.length > 75
                                ? `Goal: ${title.substring(0, 75)}…`
                                : `Goal: ${title}`}
                        </h6>
                    </div>

                    {/* RIGHT SIDE */}
                    <div onClick={(e) => e.stopPropagation()}>
                        {depth > 0 && enableSearch && !isReply && (
                            historyLoading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                            ) : (
                                <Dropdown
                                    onSelect={(eventKey) =>
                                        handleSelectGoal(
                                            goalHistory.find((g) => g.id === eventKey)
                                        )
                                    }
                                >
                                    <Dropdown.Toggle variant="outline-primary" size="sm">
                                        Select Goal
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
                                        {goalHistory.map((g) => (
                                            <Dropdown.Item key={g.id} eventKey={g.id}>
                                                {g.description?.text || "Goal"} ·{" "}
                                                {g.lifecycleStatus}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )
                        )}
                    </div>
                </Card.Header>

                {/* BODY */}
                {depth > 0 && (
                    <Card.Body>
                        {error ? (
                            <p className={styles.errorText}>{error}</p>
                        ) : (
                            <ListGroup variant="flush">
                                {fieldRenderers.slice(0, depth)}
                            </ListGroup>
                        )}
                    </Card.Body>
                )}
            </Card>

            {/* MODAL VIEWER */}
            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalGoalDetails}
                fhirClient={fhirClient}
            />
        </>
    );
}

export default GoalCard;
